import { AdminOrganisationsResponse, AdminOrganisationListItem } from '@fsp/types';
import HandshakeTwoTone from '@mui/icons-material/HandshakeTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OrganisationChip from '../../../components/OrganisationChip';
import PaperSection from '../../../components/PaperSection';
import { apiClient } from '../../../services/apiClient';
import { formatDate } from '@fsp/shared';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';

export default function OrganisationsList() {
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState<AdminOrganisationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrganisations();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage]);

  const fetchOrganisations = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const offset = page * rowsPerPage;
      params.append('limit', rowsPerPage.toString());
      params.append('offset', offset.toString());

      const response = await apiClient.get(`/admin/organisations?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch organisations');
      }

      const data: AdminOrganisationsResponse = await response.json();
      setOrganisations(data.organisations);
      setTotalCount(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching organisations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when search changes
  };

  return (
    <PaperSection title="Organisations">
      <Box sx={{ mb: 3 }}>
        <TextField
          sx={{ minWidth: '400px' }}
          name="organisationSearch"
          id="organisationSearch"
          placeholder="Search by name, legal name, or ABN..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Legal Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Subscription Status</TableCell>
                  <TableCell align="center">Affiliate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organisations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No organisations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  organisations.map((org) => (
                    <TableRow
                      key={org.id}
                      onClick={() => navigate(`/app/organisations/${org.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>
                        <OrganisationChip organisation={org} clickable={false} />
                      </TableCell>
                      <TableCell>{org.legalName}</TableCell>
                      <TableCell>{formatDate(org.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={org.subscriptionStatus}
                          color={getSubscriptionStatusColor(org.subscriptionStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {org.affiliateName && (
                          <Tooltip title={org.affiliateName}>
                            <HandshakeTwoTone
                              fontSize="small"
                              color="success"
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}
    </PaperSection>
  );
}
